import type { ActionFor, AppSubjectName } from "@msuite/citadel";
import { useCitadel } from "@/hooks/use-citadel";

/** Props Interface */
interface CanProps<S extends AppSubjectName> {
	action: ActionFor<S>;
	subject: S;
	resource?: Record<string, unknown>;
	children: React.ReactNode;
	/** Rendered while the rule list is still loading. */
	loading?: React.ReactNode;
	/** Rendered when the rule list could not be loaded and no cached rules exist. */
	error?: React.ReactNode;
	/** Rendered when the rules deny the action. */
	fallback?: React.ReactNode;
}

export function Can<S extends AppSubjectName>({
	action,
	subject,
	resource,
	children,
	loading = null,
	error = null,
	fallback = null,
}: CanProps<S>) {
	/** Query */
	const { citadel, isError } = useCitadel();

	/** Guard */
	// Three distinct states, so a screen degrades to its permitted subset: a
	// load in flight is not an error, and an error is not a denial. A cached
	// citadel outranks a failed refetch — the query keeps its data when a
	// background refetch fails, and one transient blip must not blank every
	// gated element — so the error node shows only when there are no rules to
	// answer with at all.
	if (!citadel) return isError ? <>{error}</> : <>{loading}</>;
	if (citadel.cannot(action, subject, resource)) return <>{fallback}</>;

	/** Render */
	return <>{children}</>;
}
